import { useState } from 'react';
import {
  Input,
  Select,
  SimpleGrid,
  VStack,
  HStack,
  Radio,
  RadioGroup,
  Divider,
  Text,
  Checkbox,
  Button,
  Collapse,
  Box,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';

import { SectionCard, ValidatedField } from '../../components';
import {
  useAnswerCodes,
  useQuoteLock,
  ValidationEntityProvider,
} from '../../hooks';
import type { Location } from 'api';

interface AreaCardProps {
  location: Location;
  locationIndex: number;
  onUpdate: (index: number, field: keyof Location, value: unknown) => void;
  isUpdating?: boolean;
  height?: string;
}

const hasAdditionalFieldValues = (loc: Location): boolean => {
  return !!(
    loc.block_watch ||
    loc.walled_community ||
    loc.deadbolt_locks ||
    loc.bars_on_windows ||
    loc.sprinklers ||
    loc.number_of_smoke_detectors ||
    loc.number_of_fire_extinguishers
  );
};

const AreaCard = ({
  location,
  locationIndex,
  onUpdate,
  isUpdating,
  height,
}: AreaCardProps) => {
  const { getOptions, shouldShowConditionalField } = useAnswerCodes();
  const { isLocked } = useQuoteLock();

  const isFieldLocked = (field: string) =>
    location.itv_locked_fields?.includes(field) ?? false;
  const [showAdditional, setShowAdditional] = useState(() =>
    hasAdditionalFieldValues(location),
  );

  const showFinishedBasementPercentage = shouldShowConditionalField(
    'finished_basement_percentage' as any,
    location.building_foundation_type,
  );

  const areaUnit = location.area_unit || 'sq_ft';

  const handleAreaChange =
    (field: 'total_living_area_sq_ft' | 'basement_area_sq_ft') =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value === '' ? null : parseInt(e.target.value, 10);
      onUpdate(locationIndex, field, isNaN(value as number) ? null : value);
    };

  const handleUnitChange = (newUnit: string) => {
    onUpdate(locationIndex, 'area_unit', newUnit);
  };

  const handlePercentageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value === '' ? null : parseInt(e.target.value, 10);
    if (value !== null && (value < 0 || value > 100)) return;
    onUpdate(
      locationIndex,
      'finished_basement_percentage',
      isNaN(value as number) ? null : value,
    );
  };

  const handleSelectChange =
    (field: keyof Location) => (e: React.ChangeEvent<HTMLSelectElement>) => {
      onUpdate(locationIndex, field, e.target.value);
    };

  const handleCheckboxChange =
    (field: keyof Location) => (e: React.ChangeEvent<HTMLInputElement>) => {
      onUpdate(locationIndex, field, e.target.checked);
    };

  const handleNumberChange =
    (field: keyof Location) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value === '' ? null : parseInt(e.target.value, 10);
      onUpdate(locationIndex, field, isNaN(value as number) ? null : value);
    };

  return (
    <ValidationEntityProvider
      entity={location as unknown as Record<string, unknown>}
    >
      <SectionCard title="Area" height={height}>
        <VStack spacing={2} align="stretch">
          <SimpleGrid columns={{ base: 1 }} spacing={4}>
            <ValidatedField
              itemPath="location"
              fieldPath="total_living_area_sq_ft"
              label="Total Living Area"
              isDisabled={
                isUpdating || isFieldLocked('total_living_area_sq_ft')
              }
            >
              <Input
                variant="main"
                size="sm"
                type="number"
                min={0}
                value={location.total_living_area_sq_ft ?? ''}
                onChange={handleAreaChange('total_living_area_sq_ft')}
                isDisabled={
                  isUpdating || isFieldLocked('total_living_area_sq_ft')
                }
              />
            </ValidatedField>

            <ValidatedField
              itemPath="location"
              fieldPath="basement_area_sq_ft"
              label="Basement Area"
              isDisabled={isUpdating}
            >
              <Input
                variant="main"
                size="sm"
                type="number"
                min={0}
                value={location.basement_area_sq_ft ?? ''}
                onChange={handleAreaChange('basement_area_sq_ft')}
                isDisabled={isUpdating}
              />
            </ValidatedField>
          </SimpleGrid>

          <RadioGroup
            value={areaUnit}
            onChange={handleUnitChange}
            isDisabled={isUpdating}
          >
            <HStack spacing={6}>
              <Radio value="sq_ft">sq. ft.</Radio>
              <Radio value="sq_m">sq. m.</Radio>
            </HStack>
          </RadioGroup>

          {showFinishedBasementPercentage && (
            <ValidatedField
              itemPath="location"
              fieldPath="finished_basement_percentage"
              label="Basement % Finished"
              isDisabled={
                isUpdating || isFieldLocked('finished_basement_percentage')
              }
            >
              <Input
                variant="main"
                size="sm"
                type="number"
                min={0}
                max={100}
                value={location.finished_basement_percentage ?? ''}
                onChange={handlePercentageChange}
                isDisabled={
                  isUpdating || isFieldLocked('finished_basement_percentage')
                }
                placeholder="0-100"
              />
            </ValidatedField>
          )}

          <Divider />

          <Text fontWeight="medium" fontSize="sm" color="gray.700">
            Security
          </Text>

          <SimpleGrid columns={{ base: 1 }} spacing={2}>
            <ValidatedField
              itemPath="location"
              fieldPath="burglar_alarm"
              label="Burglar Alarm"
              isDisabled={isUpdating}
            >
              <Select
                variant="main"
                size="sm"
                value={location.burglar_alarm || ''}
                onChange={handleSelectChange('burglar_alarm')}
                isDisabled={isUpdating}
              >
                {getOptions('burglar_alarm').map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </ValidatedField>

            <ValidatedField
              itemPath="location"
              fieldPath="fire_alarm"
              label="Fire Alarm"
              isDisabled={isUpdating}
            >
              <Select
                variant="main"
                size="sm"
                value={location.fire_alarm || ''}
                onChange={handleSelectChange('fire_alarm')}
                isDisabled={isUpdating}
              >
                {getOptions('fire_alarm').map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </ValidatedField>
          </SimpleGrid>

          <Divider />

          <Button
            variant="outline"
            onClick={() => setShowAdditional(!showAdditional)}
            rightIcon={showAdditional ? <ChevronUpIcon /> : <ChevronDownIcon />}
            justifyContent="space-between"
            fontWeight="normal"
          >
            Additional
          </Button>

          <Collapse in={showAdditional || isLocked} animateOpacity>
            <Box pt={2}>
              <VStack spacing={4} align="stretch">
                <SimpleGrid columns={{ base: 1 }} spacing={3}>
                  <Checkbox
                    isChecked={location.block_watch ?? false}
                    onChange={handleCheckboxChange('block_watch')}
                    isDisabled={isUpdating}
                  >
                    Block Watch
                  </Checkbox>

                  <Checkbox
                    isChecked={location.walled_community ?? false}
                    onChange={handleCheckboxChange('walled_community')}
                    isDisabled={isUpdating}
                  >
                    Walled Community
                  </Checkbox>

                  <Checkbox
                    isChecked={location.deadbolt_locks ?? false}
                    onChange={handleCheckboxChange('deadbolt_locks')}
                    isDisabled={isUpdating}
                  >
                    Deadbolt Locks
                  </Checkbox>

                  <Checkbox
                    isChecked={location.bars_on_windows ?? false}
                    onChange={handleCheckboxChange('bars_on_windows')}
                    isDisabled={isUpdating}
                  >
                    Bars on Windows
                  </Checkbox>

                  <Checkbox
                    isChecked={location.sprinklers ?? false}
                    onChange={handleCheckboxChange('sprinklers')}
                    isDisabled={isUpdating}
                  >
                    Sprinkler System
                  </Checkbox>
                </SimpleGrid>

                <SimpleGrid columns={{ base: 1 }} spacing={4}>
                  <ValidatedField
                    itemPath="location"
                    fieldPath="number_of_smoke_detectors"
                    label="# Smoke Detectors"
                    isDisabled={isUpdating}
                  >
                    <Input
                      variant="main"
                      size="sm"
                      type="number"
                      min={0}
                      value={location.number_of_smoke_detectors ?? ''}
                      onChange={handleNumberChange('number_of_smoke_detectors')}
                      isDisabled={isUpdating}
                    />
                  </ValidatedField>

                  <ValidatedField
                    itemPath="location"
                    fieldPath="number_of_fire_extinguishers"
                    label="# Fire Extinguishers"
                    isDisabled={isUpdating}
                  >
                    <Input
                      variant="main"
                      size="sm"
                      type="number"
                      min={0}
                      value={location.number_of_fire_extinguishers ?? ''}
                      onChange={handleNumberChange(
                        'number_of_fire_extinguishers',
                      )}
                      isDisabled={isUpdating}
                    />
                  </ValidatedField>
                </SimpleGrid>
              </VStack>
            </Box>
          </Collapse>
        </VStack>
      </SectionCard>
    </ValidationEntityProvider>
  );
};

export default AreaCard;
